import React, { useState } from 'react';
import './AssistantMessageExtras.css';
import useIsMobile from '../../hooks/useIsMobile';
import { useConstants } from '../../constant/constantProvider';
import GenericTooltip from '../GenericTooltip/GenericTooltip';

import blueRightIcon from '../../assets/new-images/arrow-up.svg';

const AssistantMessageExtras = ({
  suggestions = [],
  citations = [],
  sourceLinks = [],
  onSuggestionClick,
  onCitationClick,
  generatedBy,
  discussionPoints = [],
  isThinking = false,
  isWebSearch = false,
  isImageGeneration = false,
  hideMetadata = false,
  isLoading = false,
  children,
}) => {
  const isMobile = useIsMobile();
  const { CITATION, SOURCES, EXPLORE_FURTHER, SEE_MORE, SEE_LESS, GENERATE_BY, DISCUSSION_POINTS, THINKING_LABEL, WEB_SEARCH_LABEL } = useConstants();
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  // Defensive check for potential null values passed from parent
  // Flatten suggestions if they are passed as category objects {label, prompts}
  const safeSuggestions = (suggestions || []).flatMap(s => 
    typeof s === 'string' ? [s] : (Array.isArray(s?.prompts) ? s.prompts : [])
  );
  const safeCitations = citations || [];
  const safeSourceLinks = sourceLinks || [];
  const safeDiscussionPoints = discussionPoints || [];

  if (!safeSuggestions.length && !safeCitations.length && !safeSourceLinks.length && !children && !generatedBy && !safeDiscussionPoints.length && !isThinking && !isWebSearch) {
    return null;
  }

  const maxSources = isMobile ? 2 : 4;
  const visibleSources = sourcesExpanded ? safeSourceLinks : safeSourceLinks.slice(0, maxSources);

  return (
    <div className="assistant-message-extras">
      {/* Citations */}
      {safeCitations.length > 0 && (
        <div className="extras-citation-section">
          <div className="extras-label">{CITATION}</div>
          <div className="extras-citation-list">
            {safeCitations.map((item, idx) => (
              <button
                key={idx}
                type="button"
                className="extras-citation-chip extras-chip"
                onClick={() => onCitationClick && onCitationClick(item)}
              >
                {typeof item === 'string' ? item : (item?.filename || item?.text || '')}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Discussion Points */}
      {safeDiscussionPoints.length > 0 && (
        <div className="extras-discussion-section">
          <div className="extras-label">{DISCUSSION_POINTS || 'Discussion Points'}</div>
          <ul className="extras-discussion-list">
            {safeDiscussionPoints.map((item, idx) => (
              <li key={idx} className="extras-discussion-item">
                {typeof item === 'string' ? item : (item?.text || item?.content || '')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sources */}
      {safeSourceLinks.length > 0 && (
        <div className="extras-sources-section">
          <div className="extras-sources-header">
            <span className="extras-label">{SOURCES}</span>
            {safeSourceLinks.length > maxSources && (
              <button
                type="button"
                className="extras-toggle-btn"
                onClick={() => setSourcesExpanded(!sourcesExpanded)}
              >
                {sourcesExpanded ? SEE_LESS : SEE_MORE}
              </button>
            )}
          </div>
          <div className="extras-sources-list">
            {visibleSources.map((item, idx) => (
              <GenericTooltip
                key={idx}
                tooltipText={item.title}
                tooltipPosition="top"
              >
                <a
                  href={item.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="extras-source-card"
                >
                  {item.title}
                </a>
              </GenericTooltip>
            ))}
          </div>
        </div>
      )}

      {children}
 
      {!hideMetadata && (generatedBy || isThinking || isWebSearch) && (
        <div className="assistant-metadata-wrapper">
          {generatedBy && (
            <div className="generated-by-modal">
              {GENERATE_BY} {generatedBy}
            </div>
          )}
          <div className="metadata-chips-inner">
            {isThinking && (
              <div className="metadata-chip">
                {THINKING_LABEL}
              </div>
            )}
            {isWebSearch && (
              <div className="metadata-chip">
                {WEB_SEARCH_LABEL}
              </div>
            )}
          </div>
        </div>
      )}
 
       {/* Suggestions */}
      {safeSuggestions.length > 0 && !isImageGeneration && (
        <div className="extras-suggestions-section">
          <div className="extras-label">{EXPLORE_FURTHER}</div>
          <div className="extras-suggestions-list">
            {safeSuggestions.slice(0, isMobile ? 3 : 4).map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                className={`extras-suggestion-chip extras-chip ${isLoading ? 'disabled' : ''}`}
                disabled={isLoading}
                onClick={() => !isLoading && onSuggestionClick && onSuggestionClick(suggestion)}
              >
                <span>{suggestion}</span>
                <img className="arrow-icon" src={blueRightIcon} alt="right-icon" />
              </button>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default AssistantMessageExtras;
